import {Debaters} from "./Debaters.js";
import {Chats} from "./Chats.js";
import {Messages} from "./Messages.js";
import {chatClientState} from "./ChatClientState.js";
import {ChatUI} from "./ChatUI.js";
import type {WsRecipient} from "../ws/WsRecipient.ts";
import crmWS from "../ws/UniversalSocket.ts";
import {msg} from "@/i18n/Message.ts";
import {id} from "@/utils/Util";

const NUMS = ["0", "❶", "❷", "❸", "❹", "❺", "❻", "❼", "❽", "❾", "❿"];


export interface SharedContextDirect {
	url: string;      // URL of the page
	title: string;    // Name of the context
	info?: string;    // additional short info
	icon?: string;    // optional icon
	thumbnail?: string; // optional image (thumbnail)
}

export type SharedContext = SharedContextDirect | (() => SharedContextDirect);

export class ChatClient implements WsRecipient {

	private container:JQuery;
	private me:string;
	private ui:ChatUI;
	private windowTitle:string;
	private visibility:boolean;
	private context:SharedContext;

	/**
	 * @param {jQuery} containerElement - element which will be container of the client
	 * @param {string} me - nick of the current user (not party ID)
	 * @param {SharedContext} [sharedContext] - context object
	 */
	constructor(containerElement:JQuery, me:string, sharedContext:SharedContext) {
		this.container = containerElement;
		this.me = me;
		/**
		 * @type {ChatUI}
		 */
		this.ui = null;

		this.windowTitle = document.title;
		this.visibility = true;	// page is visible
		this.context = sharedContext;
		chatClientState.clearIfNotUser(me);
		crmWS.register("chat", this);
		this.install();
		this.hookVisibilityHandler();
		this.updateUnseenBadges(this.unseen);
	}

	get unseen() {
		return chatClientState.unseen;
	}

	set unseen(u) {
		chatClientState.unseen = u;
		this.updateUnseenBadges(u);
	}

	onMessage(sender, message) {
		const eventId = message.id;
		let chatId = message.chat;
		if (this.ui) {
			switch (message.type) {	// See Java type ChatEvent.Type
				case "NEW_MESSAGE":
					this.ui.newMessageInChat(chatId, message.data);
					this.playSound();
					break;
				case "TYPING_START":
					this.ui.typingStarted(chatId, message.data);
					break;
				case "TYPING_END":
					this.ui.typingEnded(chatId, message.data);
					break;
				case "STATE": // some user changed the state
					this.ui.debaterStateChanged(message.data.from, message.data.state);
					break;
				case "DEBATER_ENTER":
					break;
				case "DEBATER_LEAVE":
					break;
				default:
					console.log("Unsupported socket event " + message.type);
			}
		} else {
			if (message.type === "NEW_MESSAGE") {
				this.newMessageWhenCollapsed(chatId, message.data);
				this.playSound();
			}
		}
		chatClientState.lastEvent = eventId;
	}

	updateUnseenBadges(u) {
		if (this.badge) {
			this.badge.text(u);
			if (u > 0) this.badge.show();
			else this.badge.hide();
		}
		if (u > 0) {
			let html;
			if (u < NUMS.length) html = NUMS[u];
			else html = "["+u+"]"
			document.title = html + " " + this.windowTitle;
			if (this.container) {
				this.container.addClass("chat-new-messages");
			}
		} else {
			document.title = this.windowTitle;
			if (this.container) {
				this.container.removeClass("chat-new-messages");
			}
		}
	}

	newMessageWhenCollapsed(chatId, data) {
		this.unseen = this.unseen + 1;
		if (this.ui == null) {
			chatClientState.ui = "messages";
			chatClientState.currentChat = chatId;	// open in this chat
		}
	}

	sendASAP(msg) {
		crmWS.send("chat", "chat", msg);
	}

	requestMissedEvents() {
		const eventId = chatClientState.lastEvent;
		if (eventId != null) this.sendASAP({
			type: "REPLAY",
			from: this.me,
			after: eventId
		});
	}

	playSound() {
		if (document.visibilityState !== "visible" || this.ui == null /* chat collapsed */) {
			try {
				if (!this.audio) this.audio = new Audio('sound/new-chat-message.mp3');
				this.audio.play().catch(reason => {
					console.log("Sound play not allowed " + reason);
				});
			} catch(e) {
				// Ignore errors
			}
		}
	}

	goingToSend(msg) {
	}

	typingEvent(eventName) {
		const msg = {
			type: eventName,
			from: this.me,
			chat: this.ui.chatId
		};
		crmWS.send("chat", "chat", msg);
	}

	typingStart(e) {
		this.typingEvent("TYPING_START");
	}

	typingEnd(e) {
		this.typingEvent("TYPING_END");
	}

	install() {
		this.container.empty();
		// position
		const collapsed = chatClientState.collapsed;

		this.container.css("top", chatClientState.top);
		if (collapsed) {
			this.ui = null;
			this.container.css("bottom", "auto");
			this.container.addClass("chat-collapsed");
			this.headerContainer = $("<div class='chat-header-collapsed'></div>");
			this.container.append(this.headerContainer);

			const icn = $(Icon.fi("chat"));
			this.headerContainer.append(icn);

			const title = $("<span class='chat-header-title'></span>");
			title.text("Chat");
			this.headerContainer.append(title);

			this.badge = $("<span class='chat-header-badge'></span>");
			this.headerContainer.append(this.badge);
			if (this.unseen > 0) {
				this.badge.text(this.unseen);
			} else this.badge.hide();

			this.headerContainer.on("click", this.expandChat.bind(this));

			this.mainContainer = null;
			chatClientState.data = null;	// force load when expanded
		} else {
			this.badge = null;
			this.container.css("bottom", chatClientState.bottom);
			this.container.removeClass("chat-collapsed");

			this.headerContainer = $("<div class='chat-header'></div>");
			const hrow = $("<div class='chat-header-row'></div>");
			this.headerContainer.append(hrow);
			this.headerUiContainer = $("<div class='chat-header-ui'></div>");
			hrow.append(this.headerUiContainer);
			this.container.append(this.headerContainer);

			this.collapseButton = $(Icon.fi("collapse-right", "link-like-fi"));
			hrow.append(this.collapseButton);
			this.collapseButton.on("click", this.collapseChat.bind(this));

			// Create layout
			this.headerResizer = $("<div class='chat-resizer' data-resizer='top'></div>");
			this.headerContainer.prepend(this.headerResizer);

			this.mainContainer = $("<div class='chat-main'></div>");
			this.container.append(this.mainContainer);

			this.footerContainer = $("<div class='chat-footer'></div>");
			this.footerUiContainer = $("<div class='chat-footer-ui'></div>");
			this.footerContainer.append(this.footerUiContainer);
			this.footerResizer = $("<div class='chat-resizer' data-resizer='bottom'></div>");
			this.footerContainer.append(this.footerResizer);

			this.container.append(this.footerContainer);

			// Controllers
			this.installMain();
			this.hookResizers();
		}
		this.requestMissedEvents();
		this.container.css("visibility", "visible");
	}

	hookResizers() {
		this.headerResizer.on("mousedown", (e) => {
			this.resizeClient(e, "top");
		});
		this.footerResizer.on("mousedown", (e) => {
			this.resizeClient(e, "bottom");
		});
	}

	resizeClient(e, handler) {
		const wh = window.innerHeight;
		const windowTop = $(window).scrollTop();

		if ("top" === handler) {
			const clientTop = this.container.offset().top - windowTop;
			this.mouseY = e.clientY;
			this.clientY = clientTop;
		} else {
			const clientBottom = wh - (this.container.offset().top - windowTop + this.container.outerHeight(false));
			this.mouseY = e.clientY;
			this.clientY = clientBottom;
		}

		//console.log("DRAG START y=" + this.mouseY + " top=" + this.clientY);
		e.preventDefault();
		$("body")
			.on("mousemove.chat", (e) => {
				const pxDiff = e.clientY - this.mouseY;
				const minDist = (800/wh);	// 8px
				if ("top" === handler) {
					const y = this.clientY + pxDiff;
					const yVH = Math.max((100*y / wh), minDist);
					this.container.css(handler, yVH + "vh");
					chatClientState.top = yVH+"vh";
				} else {
					const y = this.clientY - pxDiff;
					const yVH = Math.max((100*y / wh), minDist);
					this.container.css(handler, yVH + "vh");
					chatClientState.bottom = yVH + "vh";
				}
				e.preventDefault();
			})
			.on("mouseup.chat", (e) => {
				$("body").off(".chat");
			});
	}

	installMain() {
		this.headerUiContainer.empty();
		this.mainContainer.empty();
		this.footerUiContainer.empty();

		let uiName = chatClientState.ui;
		if (uiName == null) uiName = "chats";

		switch (uiName) {
			case "debaters":
				this.installDebaters();
				break;
			case "messages":
				const chatId = chatClientState.currentChat;
				if (chatId != null) {
					this.installMessages(chatId);
					break;
				}
			case "chats":
				this.installChats();
				break;
		}
	}

	installBackButton() {
		const icn = $(Icon.fi("boxed-arrow-left", "link-like-fi"));
		this.headerUiContainer.append(icn);
		icn.on("click", this.returnToChats.bind(this));
	}

	installDebaters() {
		this.installBackButton();
		this.ui = new Debaters(this, this.headerUiContainer, this.mainContainer, this.footerUiContainer, this.me);
		this.ui.addEventListener("cancel", this.returnToChats.bind(this));
		this.ui.addEventListener("created", this.newChatCreated.bind(this));
	}

	installMessages(id) {
		this.installBackButton();
		this.ui = new Messages(this, this.headerUiContainer, this.mainContainer, this.footerUiContainer, this.me, this.context);
		this.ui.addEventListener("send", this.goingToSend.bind(this));
		this.ui.addEventListener("close", this.returnToChats.bind(this));
		this.ui.messageEditor.addEventListener("typingstart", this.typingStart.bind(this));
		this.ui.messageEditor.addEventListener("typingend", this.typingEnd.bind(this));
		this.ui.showChat(id);
	}

	installChats() {
		this.ui = new Chats(this, this.headerUiContainer, this.mainContainer, this.footerUiContainer, this.me);
		this.ui.addEventListener("chatselect", this.chatSelected.bind(this));
		this.ui.addEventListener("newchat", this.newChat.bind(this));
	}

	newChatCreated(data) {
		chatClientState.ui = "messages";
		chatClientState.currentChat = data.id;
		this.installMain();
	}

	returnToChats() {
		chatClientState.ui = "chats";
		chatClientState.currentChat = null;
		this.installMain();
	}


	chatSelected(e) {
		chatClientState.ui = "messages";
		chatClientState.currentChat = e.chat;
		this.installMain();
	}

	newChat(e) {
		chatClientState.ui = "debaters";
		this.installMain();
	}

	collapseChat() {
		chatClientState.collapsed = true;
		this.install();
	}

	expandChat() {
		chatClientState.collapsed = false;
		this.install();
	}

	hookVisibilityHandler() {
		document.addEventListener("visibilitychange", function() {
			if (document.visibilityState === 'visible') {
				if (this.ui != null) this.ui.visible(true);
			} else {
				if (this.ui != null) this.ui.visible(false);
			}
		}.bind(this));
	}

	onVisibilityChange(evt) {
		const evtMap = {
			focus:true, focusin:true, pageshow:true, blur:false, focusout:false, pagehide:false
		};

		evt = evt || window.event;
		if (evt.type in evtMap) this.visibility = evtMap[evt.type];
	}

	onClose() {};
	onOpen() {};

}
